import { Countries } from '@/comm'
import { useDatePicker } from '@/components/MyDatePicker'
import { usePid2 } from '@/components/DropdownPid'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { formatPercent, formatUSD } from '@/lib/format-utils'
import { getDateRange } from '@/lib/date-utils'

import { Button } from '@/components/ui/button'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'
import {
    useColumnFilters,
    VSWeekSelector,
} from '@/components/data-table-selectors'
import useSWR from 'swr'
import Td2 from '../../../components/zthd/Td2'
import { columnDate } from './columns'

const useColumnVisibilityStore = createColumnVisibilityStore({}, 'ccr')

const countryColumn = (code, name) => ({
    id: `${name}`,
    header: `${name}`,
    accessorFn: (row) => {
        const curr = row.datas?.[code]
        const total = row.datas?.total
        return { curr, total }
    },
    cell: (row) => {
        const { curr, total } = row.getValue()
        const formatted = formatUSD(curr, 0, 0)
        const per = formatPercent(curr / total)
        return <Td2 line2={per}>{formatted}</Td2>
    },
})

const Home = () => {
    const startDate = useDatePicker((state) => state.startDate)
    const endDate = useDatePicker((state) => state.endDate)
    const pids = usePid2((state) => state.pids)

    const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
    const columnFilters = useColumnFilters((state) => state.columnFilters)

    const { data: revenues, mutate } = useSWR([
        '/revenues/countrycontrast',
        { startDate, endDate, pid: pids.join(',') },
    ])

    const data = useMemo(() => {
        const dateRange = getDateRange(dayjs(startDate), dayjs(endDate))

        return dateRange.map((date) => {
            const originalData = revenues?.[date]

            if (!originalData) {
                return {
                    date,
                    datas: originalData,
                }
            }

            // Add total calculation
            const processedData = { ...originalData }
            processedData.total = Object.values(originalData).reduce((sum, value) => {
                return sum + (value || 0)
            }, 0)

            return {
                date,
                datas: processedData,
            }
        })
    }, [startDate, endDate, revenues])

    const columns = useMemo(() => {
        const countries = Countries.filter(({ code }) => code !== 'total')
        const rows = []
        rows.push(columnDate)
        rows.push(countryColumn('total', 'Total'))
        countries.forEach((country) => {
            const { code, name } = country
            rows.push(countryColumn(code, name))
        })
        return rows
    }, [])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        enableMultiRowSelection: true,
        state: {
            columnFilters,
            columnVisibility,
        },
    })

    return (
        <>
            <div className="flex items-center xs:gap-1 xs:py-1 sm:gap-2 sm:py-2 gap-4 py-4   flex-wrap">
                <VSWeekSelector />
                <Button variant="outline" onClick={mutate}>
                    refresh
                </Button>
                <DataTableViewOptions table={table} />
            </div>
            <DataTableWithBorder table={table} />
        </>
    )
}

export default Home 